import React, { useCallback, useMemo } from "react";
import { extractDateFromUnknownFormat } from "@common/helpers/date";
import { formatSpreadsheetDateValue } from "@common/helpers/spreadsheet";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { Account } from "@common/types/domains/accounting/account";
import { displayAccountLabel } from "@common/helpers/accounting";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { isFunction } from "@common/helpers/typeGuards";
import { useClasses } from "@api/class.api";
import { ChangedCells } from "@components/spreadsheet/SpreadsheetGrid";
import { CellType } from "./cells";
import { createFinancialAccountClipboard } from "./cells/financialAccountClipboard";
import {
    ColumnDef,
    SelectionRange,
    SpreadsheetCell,
    SpreadsheetData,
} from "./types";
import { isSpreadsheetActionsColumn } from "./spreadsheetActionsColumn";

interface UseSpreadsheetCopyPasteProps {
    data: SpreadsheetData;
    /** Rows in on-screen order (expanded tree rows flattened); copy reads selected indexes from these. */
    visibleRows: SpreadsheetData;
    columns: ColumnDef[];
    selectedCell: SpreadsheetCell | null;
    selectionRange: SelectionRange;
    isEditing: boolean;
    onChange: (newData: SpreadsheetData, changedCells: ChangedCells) => void;
    emptySpareRows?: boolean;
}

type SpreadsheetValue = string | number | boolean | string[] | null;

interface ValueTransformer {
    toClipboard: (value: SpreadsheetValue) => string;
    fromClipboard: (text: string) => SpreadsheetValue;
}

const defaultTransformer: ValueTransformer = {
    toClipboard: (value) => String(value ?? ""),
    fromClipboard: (text) => text,
};

export function useSpreadsheetCopyPaste({
    data,
    visibleRows,
    columns,
    selectedCell,
    selectionRange,
    isEditing,
    onChange,
    emptySpareRows,
}: UseSpreadsheetCopyPasteProps) {
    const copyToClipboard = useCopyToClipboard();
    const showAccountNumbers = useShowAccountNumbers();
    const categoriesById = useCategoriesById();
    const entities = columns.find(
        (column) => column.cellType === CellType.ENTITY,
    )?.entities;
    const { entitiesByIdMap, entitiesByNameMap } = useMemo(() => {
        const byIdMap = new Map<number, { id: number; name: string }>();
        const byNameMap = new Map<string, { id: number; name: string }>();

        if (!entities) {
            return {
                entitiesByIdMap: byIdMap,
                entitiesByNameMap: byNameMap,
            };
        }

        entities.forEach((entity: { id: number; name: string }) => {
            byIdMap.set(entity.id, entity);
            byNameMap.set(entity.name.toLowerCase(), entity);
        });

        return {
            entitiesByIdMap: byIdMap,
            entitiesByNameMap: byNameMap,
        };
    }, [entities]);
    const { classesWithChildren } = useClasses();
    const { classesByIdMap, classesByLabelMap } = useMemo(() => {
        const byIdMap = new Map<string, string>();
        const byLabelMap = new Map<string, string>();

        classesWithChildren.forEach((classWithChildren) => {
            const parentLabel = classWithChildren.label;
            byIdMap.set(classWithChildren.id, parentLabel);
            byLabelMap.set(parentLabel, classWithChildren.id);

            classWithChildren.children.forEach((child) => {
                const childLabel = `${parentLabel}>${child.label}`;
                byIdMap.set(child.id, childLabel);
                byLabelMap.set(childLabel, child.id);
            });
        });

        return { classesByIdMap: byIdMap, classesByLabelMap: byLabelMap };
    }, [classesWithChildren]);

    const accounts = columns.find(
        (column) => column.cellType === CellType.ACCOUNT_SEARCH,
    )?.accounts;
    const {
        accountsByIdMap,
        accountsByLabelMap,
        accountsByCodeMap,
        accountsByNameMap,
    } = useMemo(() => {
        const byIdMap = new Map<string, Account>();
        const byLabelMap = new Map<string, Account>();
        const byCodeMap = new Map<string, Account>();
        const byNameMap = new Map<string, Account>();
        if (!accounts) {
            return {
                accountsByIdMap: byIdMap,
                accountsByCodeMap: byCodeMap,
                accountsByLabelMap: byLabelMap,
                accountsByNameMap: byNameMap,
            };
        }

        accounts.forEach((account: Account) => {
            byIdMap.set(account.id, account);
            byLabelMap.set(displayAccountLabel(account, true), account);
            byLabelMap.set(displayAccountLabel(account, false), account);
            if (account.codeText !== null) {
                byCodeMap.set(account.codeText, account);
            }
            byNameMap.set(String(account.name), account);
        });
        return {
            accountsByIdMap: byIdMap,
            accountsByCodeMap: byCodeMap,
            accountsByLabelMap: byLabelMap,
            accountsByNameMap: byNameMap,
        };
    }, [accounts]);

    const financialAccounts = columns.find(
        (column) => column.cellType === CellType.FINANCIAL_ACCOUNT,
    )?.financialAccounts;
    const financialAccountClipboard = useMemo(
        () => createFinancialAccountClipboard(financialAccounts ?? []),
        [financialAccounts],
    );

    const numberCopyPasteConfig: ValueTransformer = useMemo(
        () => ({
            toClipboard: (value) => String(value ?? ""),
            fromClipboard: (text) => {
                if (!text) {
                    return null;
                }

                let cleaned = text.trim();
                const isNegative =
                    cleaned.includes("(") && cleaned.includes(")");

                cleaned = cleaned
                    .replace(/[()]/g, "")
                    .replace(/[^\d,.-]/g, "")
                    .replace(/(?!^)-/g, "");

                if (!cleaned) {
                    return null;
                }

                const commaIndex = cleaned.lastIndexOf(",");
                const dotIndex = cleaned.lastIndexOf(".");

                if (commaIndex !== -1 && dotIndex !== -1) {
                    if (commaIndex > dotIndex) {
                        cleaned = cleaned.replace(/\./g, "").replace(/,/g, ".");
                    } else {
                        cleaned = cleaned.replace(/,/g, "");
                    }
                } else if (commaIndex !== -1) {
                    cleaned = cleaned.replace(/,/g, ".");
                }

                const numericValue = parseFloat(cleaned);

                if (Number.isNaN(numericValue)) {
                    return null;
                }

                return isNegative ? -numericValue : numericValue;
            },
        }),
        [],
    );

    const transformers: Partial<Record<CellType, ValueTransformer>> = useMemo(
        () => ({
            [CellType.NUMBER]: numberCopyPasteConfig,
            [CellType.MATH]: numberCopyPasteConfig,
            [CellType.BOOLEAN]: {
                toClipboard: (value) =>
                    typeof value === "boolean" ? String(value) : "",
                fromClipboard: (text) => {
                    const normalizedText = text.trim().toLowerCase();
                    if (normalizedText === "true") {
                        return true;
                    }
                    if (normalizedText === "false") {
                        return false;
                    }
                    return null;
                },
            },
            [CellType.DATE]: {
                toClipboard: (value) => String(value ?? ""),
                fromClipboard: (text) => {
                    const date = extractDateFromUnknownFormat(text);
                    return date ? formatSpreadsheetDateValue(date) : text;
                },
            },
            [CellType.CATEGORY]: {
                toClipboard: (value) =>
                    value && categoriesById[value as string]
                        ? String(value)
                        : "",
                fromClipboard: (text) => {
                    const categoryId = text.trim();
                    if (!categoryId) {
                        return null;
                    }
                    return categoriesById[categoryId] ? categoryId : null;
                },
            },
            [CellType.ENTITY]: {
                toClipboard: (value) => {
                    if (value === null || value === undefined || value === "") {
                        return "";
                    }

                    const entityId =
                        typeof value === "number"
                            ? value
                            : Number.parseInt(String(value), 10);
                    if (Number.isNaN(entityId)) {
                        return "";
                    }

                    return entitiesByIdMap.get(entityId)?.name ?? "";
                },
                fromClipboard: (text) => {
                    const normalizedText = text.trim().toLowerCase();
                    if (!normalizedText) {
                        return null;
                    }

                    const entityFromName =
                        entitiesByNameMap.get(normalizedText);
                    if (entityFromName) {
                        return entityFromName.id;
                    }

                    const parsedEntityId = Number.parseInt(text.trim(), 10);
                    if (!Number.isNaN(parsedEntityId)) {
                        return entitiesByIdMap.has(parsedEntityId)
                            ? parsedEntityId
                            : null;
                    }

                    return null;
                },
            },
            [CellType.ACCOUNT_SEARCH]: {
                toClipboard: (value) => {
                    if (!value) {
                        return "";
                    }

                    const account = accountsByIdMap.get(String(value));
                    if (!account) {
                        return "";
                    }

                    return displayAccountLabel(account, showAccountNumbers);
                },
                fromClipboard: (text) => {
                    const labelOrCode = text.trim();
                    if (!labelOrCode) {
                        return null;
                    }

                    const accountFromLabel =
                        accountsByLabelMap.get(labelOrCode);
                    if (accountFromLabel) {
                        return accountFromLabel.id;
                    }

                    const accountFromCode = accountsByCodeMap.get(labelOrCode);
                    if (accountFromCode) {
                        return accountFromCode.id;
                    }

                    const accountFromName = accountsByNameMap.get(labelOrCode);
                    if (accountFromName) {
                        return accountFromName.id;
                    }

                    return null;
                },
            },
            [CellType.CLASS]: {
                toClipboard: (value) => {
                    if (!value || !Array.isArray(value) || value.length === 0) {
                        return "";
                    }

                    return value
                        .map((v) => classesByIdMap.get(v))
                        .filter(Boolean)
                        .join(",");
                },
                fromClipboard: (text: string) =>
                    text
                        .split(",")
                        .map((c) => classesByLabelMap.get(c))
                        .filter(Boolean) as string[],
            },
            [CellType.FINANCIAL_ACCOUNT]: financialAccountClipboard,
        }),
        [
            financialAccountClipboard,
            categoriesById,
            entitiesByIdMap,
            entitiesByNameMap,
            accountsByIdMap,
            accountsByLabelMap,
            accountsByCodeMap,
            accountsByNameMap,
            classesByIdMap,
            classesByLabelMap,
            numberCopyPasteConfig,
            showAccountNumbers,
        ],
    );

    const getTransformerForColumn = useCallback(
        (column: ColumnDef): ValueTransformer =>
            (column.cellType && transformers[column.cellType]) ??
            defaultTransformer,
        [transformers],
    );

    const isSelectionActive = useMemo(
        () =>
            selectionRange.start &&
            selectionRange.end &&
            (selectionRange.start.row !== selectionRange.end.row ||
                selectionRange.start.col !== selectionRange.end.col),
        [selectionRange],
    );

    const handleCopy = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (isEditing) {
                return;
            }

            if (!selectedCell && !isSelectionActive) {
                return;
            }

            let copyData: string[][] = [];

            if (
                isSelectionActive &&
                selectionRange.start &&
                selectionRange.end
            ) {
                const rowMin = Math.min(
                    selectionRange.start.row,
                    selectionRange.end.row,
                );
                const rowMax = Math.max(
                    selectionRange.start.row,
                    selectionRange.end.row,
                );
                const colMin = Math.min(
                    selectionRange.start.col,
                    selectionRange.end.col,
                );
                const colMax = Math.max(
                    selectionRange.start.col,
                    selectionRange.end.col,
                );

                for (let r = rowMin; r <= rowMax; r++) {
                    const row: string[] = [];
                    for (let c = colMin; c <= colMax; c++) {
                        if (isSpreadsheetActionsColumn(columns[c])) {
                            continue;
                        }
                        const field = columns[c].field;
                        const value = visibleRows[r]?.[field];
                        const columnToClipboard = columns[c].toClipboard;
                        const clipboardValue =
                            columnToClipboard?.(value, visibleRows[r]) ??
                            getTransformerForColumn(columns[c]).toClipboard(
                                value,
                            );
                        row.push(clipboardValue);
                    }
                    copyData.push(row);
                }
            } else if (selectedCell) {
                if (isSpreadsheetActionsColumn(columns[selectedCell.col])) {
                    return;
                }
                const field = columns[selectedCell.col].field;
                const value = visibleRows[selectedCell.row]?.[field];
                const columnToClipboard = columns[selectedCell.col].toClipboard;
                const clipboardValue =
                    columnToClipboard?.(value, visibleRows[selectedCell.row]) ??
                    getTransformerForColumn(
                        columns[selectedCell.col],
                    ).toClipboard(value);
                copyData = [[clipboardValue]];
            }

            if (copyData.length > 0) {
                const clipboardText = copyData
                    .map((row) => row.join("\t"))
                    .join("\n");

                copyToClipboard(clipboardText);
            }

            e.preventDefault();
        },
        [
            isSelectionActive,
            selectionRange,
            selectedCell,
            visibleRows,
            columns,
            copyToClipboard,
            getTransformerForColumn,
            isEditing,
        ],
    );

    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            if (isEditing) {
                return;
            }

            if (!selectedCell && !isSelectionActive) {
                return;
            }

            const clipboardData = e.clipboardData.getData("text/plain");
            if (!clipboardData) {
                return;
            }

            const rows = clipboardData.split(/\r?\n/).filter(Boolean);
            const parsed = rows.map((row) => row.split("\t"));

            // Calculate clipboard dimensions
            const clipboardRows = parsed.length;
            const clipboardCols = Math.max(
                ...parsed.map((row) => row.length),
                1,
            );

            let startRow: number;
            let startCol: number;
            let endRow: number;
            let endCol: number;

            if (
                isSelectionActive &&
                selectionRange.start &&
                selectionRange.end
            ) {
                // Use selection range
                startRow = Math.min(
                    selectionRange.start.row,
                    selectionRange.end.row,
                );
                endRow = Math.max(
                    selectionRange.start.row,
                    selectionRange.end.row,
                );
                startCol = Math.min(
                    selectionRange.start.col,
                    selectionRange.end.col,
                );
                endCol = Math.max(
                    selectionRange.start.col,
                    selectionRange.end.col,
                );
            } else if (selectedCell) {
                // Use single cell selection
                startRow = selectedCell.row;
                endRow = selectedCell.row + clipboardRows - 1;
                startCol = selectedCell.col;
                endCol = selectedCell.col + clipboardCols - 1;
            } else {
                return;
            }

            // Calculate selection dimensions
            const selectionRows = endRow - startRow + 1;
            const selectionCols = endCol - startCol + 1;

            // Check if selection dimensions are multiples of clipboard dimensions
            const shouldTile =
                selectionRows % clipboardRows === 0 &&
                selectionCols % clipboardCols === 0;

            const newData = [...data];
            const changedCells: ChangedCells = [];

            // Determine the actual paste range
            const pasteEndRow = shouldTile
                ? endRow
                : Math.min(startRow + clipboardRows - 1, endRow);
            const pasteEndCol = shouldTile
                ? endCol
                : Math.min(startCol + clipboardCols - 1, endCol);

            // Iterate through all cells in the paste range
            for (let r = startRow; r <= pasteEndRow; r++) {
                // Skip empty spare rows
                if (emptySpareRows && r >= data.length) {
                    continue;
                }

                for (let c = startCol; c <= pasteEndCol; c++) {
                    if (c >= columns.length) {
                        break;
                    }

                    const column = columns[c];
                    if (isSpreadsheetActionsColumn(column)) {
                        continue;
                    }
                    if (
                        column.isEditable === false ||
                        (isFunction(column.isEditable) &&
                            !column.isEditable({
                                row: r,
                                col: c,
                            }))
                    ) {
                        continue;
                    }

                    // Calculate which clipboard cell to use
                    // If tiling is enabled, use modulo; otherwise, use direct offset
                    const clipboardRowIdx = shouldTile
                        ? (r - startRow) % clipboardRows
                        : r - startRow;
                    const clipboardColIdx = shouldTile
                        ? (c - startCol) % clipboardCols
                        : c - startCol;

                    // Only paste if within clipboard bounds
                    if (
                        clipboardRowIdx >= 0 &&
                        clipboardRowIdx < clipboardRows &&
                        clipboardColIdx >= 0 &&
                        clipboardColIdx < clipboardCols
                    ) {
                        const clipboardValue =
                            parsed[clipboardRowIdx]?.[clipboardColIdx] ?? "";

                        const field = column.field;
                        const transformer = getTransformerForColumn(column);
                        const internalValue =
                            transformer.fromClipboard(clipboardValue);
                        newData[r] = {
                            ...newData[r],
                            [field]: internalValue,
                        };
                        changedCells.push([r, c]);
                    }
                }
            }

            onChange(newData, changedCells);
            e.preventDefault();
        },
        [
            isSelectionActive,
            selectedCell,
            selectionRange,
            data,
            columns,
            onChange,
            getTransformerForColumn,
            isEditing,
            emptySpareRows,
        ],
    );

    return {
        handleCopy,
        handlePaste,
    };
}
